import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { useEffect, useState } from "react";
import { Auth as AmplifyAuth, Hub } from "aws-amplify";
import { Outlet, useNavigate } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

type LoginMode = "choose" | "cognito";

export function Auth() {
  const navigate = useNavigate();
  const { userInfo, updateUserInfo } = useUserInfoContext();
  const [loginMode, setLoginMode] = useState<LoginMode>("choose");
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const applySession = (session: any) => {
      if (!session?.idToken?.payload) {
        return;
      }

      const groupsRaw = session.idToken.payload["cognito:groups"];
      const groups = Array.isArray(groupsRaw)
        ? groupsRaw
        : groupsRaw
        ? [groupsRaw]
        : [];

      const userInfo = {
        userId: session.idToken.payload["sub"] || session.idToken.payload["cognito:username"],
        accessToken: session.idToken.jwtToken,
        email: session.idToken.payload.email,
        groups,
        isAdmin: groups.includes("admin"),
      };

      updateUserInfo({ ...userInfo });
      if (window.location.pathname === "/") {
        navigate("/chat");
      }
    };

    const unsubscribe = Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
        case "cognitoHostedUI":
          applySession(data?.signInUserSession);
          navigate("/chat");
          break;
        case "signOut":
          setLoginMode("choose");
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          console.log("Error", data);
          break;
      }
    });
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [navigate, updateUserInfo]);

  async function startUciSso() {
    setSsoError(null);
    try {
      // Starts Cognito Hosted UI with the configured SAML IdP.
      await AmplifyAuth.federatedSignIn({ customProvider: "UCI-Shibboleth" });
    } catch (err) {
      console.error("Failed to start UCI SSO sign-in", err);
      setSsoError("Could not start UCI SSO login. Please try again.");
    }
  }

  return (
    <>
      {userInfo ? (
        <Outlet />
      ) : loginMode === "choose" ? (
        <div style={{ maxWidth: 420, margin: "0 auto", padding: 24 }}>
          <h2 style={{ marginTop: 0 }}>Sign in</h2>
          <p style={{ color: "#666", marginTop: 0 }}>
            Choose how you want to authenticate.
          </p>

          <div style={{ display: "grid", gap: 12 }}>
            <button
              type="button"
              onClick={() => setLoginMode("cognito")}
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Sign in with Cognito (email / password)
            </button>

            <button
              type="button"
              onClick={startUciSso}
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #0b5fff",
                background: "#0b5fff",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Sign in with UCI SSO
            </button>
          </div>

          {ssoError ? (
            <p role="alert" style={{ color: "#b00020", marginTop: 12 }}>
              {ssoError}
            </p>
          ) : null}
        </div>
      ) : (
        <div style={{ maxWidth: 420, margin: "0 auto", padding: 24 }}>
          <button
            type="button"
            onClick={() => setLoginMode("choose")}
            style={{
              marginBottom: 12,
              padding: 0,
              border: "none",
              background: "transparent",
              color: "#0b5fff",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>

          <Authenticator hideSignUp>
            <Outlet />
          </Authenticator>
        </div>
      )}
    </>
  );
}
